import { useMemo } from 'react';
import usePagination from './usePagination';
import useSorting from './useSorting';
import useFiltering from './useFiltering';
import { PAGINATION } from '../utils/constants';

/**
 * Custom hook for handling data tables with pagination, sorting, and filtering
 * @param {array} data - Array of data items
 * @param {object} options - Configuration options
 * @returns {object} Data table state and handlers
 */
const useDataTable = (data = [], options = {}) => {
  const {
    initialPage = 1,
    initialPageSize = PAGINATION.DEFAULT_PAGE_SIZE,
    initialSortField = null,
    initialSortDirection = 'asc',
    initialFilters = {},
    searchFields = [],
  } = options;
  
  // Set up filtering
  const {
    filteredItems,
    filters,
    hasActiveFilters,
    activeFilterCount,
    setFilter,
    setMultipleFilters,
    removeFilter,
    clearFilters,
    resetFilters,
  } = useFiltering(data, initialFilters);
  
  // Set up sorting (on filtered items)
  const {
    sortedItems,
    sortInfo,
    handleSortChange,
    resetSorting,
  } = useSorting(filteredItems, initialSortField, initialSortDirection);
  
  // Set up pagination (on sorted and filtered items)
  const {
    currentItems,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
    pageSizeOptions,
  } = usePagination(sortedItems, initialPage, initialPageSize);
  
  // Global search functionality
  const handleSearch = useMemo(() => {
    return (searchTerm) => {
      if (!searchTerm) {
        // Clear search filters
        const searchFilters = searchFields.reduce((acc, field) => {
          acc[field] = '';
          return acc;
        }, {});
        
        setMultipleFilters(searchFilters);
        return;
      }
      
      if (searchFields.length === 0) {
        console.warn('No search fields specified for useDataTable');
        return;
      }
      
      // Create OR condition across all search fields
      const searchFilters = searchFields.reduce((acc, field) => {
        acc[field] = searchTerm;
        return acc;
      }, {});
      
      setMultipleFilters(searchFilters);
    };
  }, [searchFields, setMultipleFilters]);
  
  // Reset all table state
  const resetTable = () => {
    resetFilters();
    resetSorting();
    resetPagination();
  };
  
  // Table state summary
  const tableInfo = useMemo(() => {
    return {
      totalItems: data.length,
      filteredItemCount: filteredItems.length,
      visibleItemCount: currentItems.length,
      hasActiveFilters,
      activeFilterCount,
      ...paginationInfo,
      ...sortInfo,
    };
  }, [
    data.length,
    filteredItems.length,
    currentItems.length,
    hasActiveFilters,
    activeFilterCount,
    paginationInfo,
    sortInfo,
  ]);
  
  return {
    // Data
    items: currentItems,
    allItems: data,
    filteredItems,
    sortedItems,
    
    // State info
    tableInfo,
    filters,
    sortInfo,
    paginationInfo,
    
    // Pagination handlers
    handlePageChange,
    handlePageSizeChange,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    pageSizeOptions,
    
    // Sorting handlers
    handleSortChange,
    
    // Filtering handlers
    setFilter,
    setMultipleFilters,
    removeFilter,
    clearFilters,
    
    // Search handler
    handleSearch,
    
    // Reset handlers
    resetFilters,
    resetSorting,
    resetPagination,
    resetTable,
  };
};

export default useDataTable;
